import { useEffect, useState } from "react";
import { getFirebaseDB, getFirebaseFunctions } from "@/lib/firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { toast } from "sonner";

interface Property {
  id: string;
  title: string;
  address: string;
  type: string;
  status: string;
  rent: number;
  createdAt: { seconds: number };
}

export default function PropertiesPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    vacant: 0,
    occupied: 0,
    maintenance: 0,
  });

  useEffect(() => {
    const db = getFirebaseDB();
    if (!db) return;

    const q = query(
      collection(db, "properties"),
      where("deleted", "!=", true),
      orderBy("deleted"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const props: Property[] = [];
      let vacant = 0;
      let occupied = 0;
      let maintenance = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        props.push({
          id: doc.id,
          title: data.title,
          address: data.address,
          type: data.type,
          status: data.status || "vacant",
          rent: data.rent,
          createdAt: data.createdAt,
        });

        if (data.status === "vacant") vacant++;
        if (data.status === "occupied") occupied++;
        if (data.status === "maintenance") maintenance++;
      });

      setProperties(props);
      setStats({
        total: props.length,
        vacant,
        occupied,
        maintenance,
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (propertyId: string, newStatus: string) => {
    try {
      const functions = getFirebaseFunctions();
      if (!functions) {
        toast.error("Firebase not initialized");
        return;
      }

      const adminOperations = httpsCallable(functions, "adminOperations");
      await adminOperations({
        action: "updatePropertyStatus",
        data: {
          propertyId,
          newStatus,
          reason: "Status updated via admin dashboard",
        },
      });
      toast.success("Property status updated");
    } catch (error) {
      toast.error("Failed to update property status");
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Properties
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Vacant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.vacant}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Occupied</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.occupied}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Maintenance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.maintenance}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Properties Table */}
      <Card>
        <CardHeader>
          <CardTitle>Properties List</CardTitle>
          <CardDescription>Manage all properties in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rent</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((property) => (
                  <TableRow key={property.id}>
                    <TableCell className="font-medium">
                      {property.title}
                    </TableCell>
                    <TableCell>{property.address}</TableCell>
                    <TableCell>{property.type}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          property.status === "vacant"
                            ? "secondary"
                            : property.status === "occupied"
                            ? "default"
                            : "destructive"
                        }
                      >
                        {property.status}
                      </Badge>
                    </TableCell>
                    <TableCell>₦{property.rent.toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleStatusChange(property.id, "occupied")
                          }
                        >
                          Occupy
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleStatusChange(property.id, "vacant")
                          }
                        >
                          Vacate
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
